import { paperClasses } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useRefreshUserInfo,
  useUserState,
} from '../../../../shared/hooks/accountHook';
import { EditModal } from '../../common/components/EditModal/EditModal';
import { CertificationButton } from '../../common/components/EditModal/styledComponents';
import { Spacer } from '../../common/components/Spacer';
import { SuccessModal } from '../../common/components/SuccessModal/SuccessModal';
import {
  SectionContentContainer,
  SectionHeading,
} from '../../common/components/styledComponents';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { EditName } from './EditName';
import { EditPassword } from './EditPassword';
import { EditPhone } from './EditPhone';
import { NotVerifiedUserInfo } from './NotVerifiedUserInfo';
import { Profile } from './Profile';
import { UserInfo } from './UserInfo';

export enum EditTarget {
  None,
  Name,
  Password,
  Phone,
  Address,
  BirthDate,
  Gender,
  Locale,
}

export const MyPage: React.FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.myPage',
  });

  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;

  const refreshUserInfo = useRefreshUserInfo();
  const [user] = useUserState();
  const [modalOpen, setModalOpen] = useState(false);
  const [target, setTarget] = useState(EditTarget.None);
  const [editSuccess, setEditSuccess] = useState(false);

  const onClickEditName = useCallback(() => {
    setModalOpen(true);
    setTarget(EditTarget.Name);
  }, []);

  const onClickEditPassword = useCallback(() => {
    setModalOpen(true);
    setTarget(EditTarget.Password);
  }, []);

  const onClickEditPhone = useCallback(() => {
    setModalOpen(true);
    setTarget(EditTarget.Phone);
  }, []);

  const onSuccessEdit = useCallback(
    (editTarget: EditTarget) => {
      setTarget(editTarget);
      setEditSuccess(true);
      refreshUserInfo();
    },
    [refreshUserInfo],
  );

  const onCloseEdit = useCallback(
    (success = true) => {
      if (!success) {
        setModalOpen(false);
        setTarget(EditTarget.None);
      }
      refreshUserInfo();
      setEditSuccess(success);
    },
    [refreshUserInfo],
  );

  const onClickConfirm = useCallback(() => {
    setModalOpen(false);
    setEditSuccess(false);
  }, []);

  const getTargetI18nKey = (successTarget: EditTarget) => {
    const prefix = 'success.targets.';
    switch (successTarget) {
      case EditTarget.Name:
        return `${prefix}name`;
      case EditTarget.Password:
        return `${prefix}password`;
      case EditTarget.Phone:
        return `${prefix}phone`;
      case EditTarget.Address:
        return `${prefix}address`;
      case EditTarget.BirthDate:
        return `${prefix}birthDate`;
      case EditTarget.Gender:
        return `${prefix}gender`;
      case EditTarget.Locale:
        return `${prefix}locale`;
    }
    return '';
  };

  return (
    <SectionContentContainer $minHeight="741px">
      <SectionHeading>{t('heading')}</SectionHeading>
      <Profile onClickEditPassword={onClickEditPassword} />
      {user?.identified ? (
        <UserInfo
          onClickEditName={onClickEditName}
          onClickEditPhone={onClickEditPhone}
          onSuccessEdit={onSuccessEdit}
        />
      ) : (
        <NotVerifiedUserInfo onSuccessEdit={onSuccessEdit} />
      )}
      {isMobile && (
        <>
          <Spacer height={24} />
          <CertificationButton onClick={onClickEditPassword}>
            {`${t('slots.password.title')}`}
          </CertificationButton>
        </>
      )}
      <EditModal open={modalOpen} onClose={() => onCloseEdit(false)}>
        <>
          {target === EditTarget.Name ? (
            <EditName onClose={onCloseEdit} />
          ) : target === EditTarget.Password ? (
            <EditPassword onClose={onCloseEdit} />
          ) : target === EditTarget.Phone ? (
            <EditPhone onClose={onCloseEdit} />
          ) : (
            <></>
          )}
        </>
      </EditModal>
      <SuccessModal
        open={editSuccess}
        onClose={() => setEditSuccess(false)}
        TransitionProps={{
          // onClose 에 넣어두면 success text 에 남아서
          // modal 이 닫힌 뒤 초기화 해줌
          onExited() {
            setTarget(EditTarget.None);
          },
        }}
        onClickConfirm={onClickConfirm}
        content={`${t('success.message', {
          target: `${t(getTargetI18nKey(target))}`,
        })}`}
      />
    </SectionContentContainer>
  );
};
